"use client";

import Link from "next/link";
import { toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  WandSparkles,
} from "lucide-react";

export default function AppDownloadPage() {
  const showComingSoonToast = (storeName: string) => {
    toast.info(`${storeName} release is coming soon`, {
      description: "We will announce as soon as the official listing is live.",
    });
  };

  return (
    <main className="relative overflow-hidden bg-[#050b16] text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_14%,rgba(255,190,11,0.24)_0%,rgba(255,190,11,0)_36%),radial-gradient(circle_at_86%_22%,rgba(56,189,248,0.18)_0%,rgba(56,189,248,0)_36%),radial-gradient(circle_at_75%_86%,rgba(34,197,94,0.14)_0%,rgba(34,197,94,0)_38%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(148,163,184,0.18)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.18)_1px,transparent_1px)] [background-size:44px_44px]" />

      <section className="relative mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-4 md:mb-5">
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-[#0f172a]/70 px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#17233b]"
          >
            <ArrowRight className="h-4 w-4 rotate-180" />
            Back to Home
          </Link>
        </div>

        <div className="mb-4 rounded-2xl border border-white/15 bg-[#0e1628]/75 px-4 py-3 backdrop-blur md:mb-5 md:px-6">
          <div className="flex flex-wrap items-center gap-3 text-sm md:text-[15px]">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#ffbf00]/20 px-3 py-1 text-[#ffd978]">
              <Sparkles className="h-4 w-4" />
              Zuma AI Mobile
            </span>
            <span className="text-[#c3d1e8]">Official Play Store listing is live</span>
            <span className="hidden h-1 w-1 rounded-full bg-[#5f769a] md:block" />
            <span className="text-[#c3d1e8]">App Store update coming soon</span>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-12 lg:gap-5 lg:items-center">
          <div className="rounded-[28px] border border-white/20 bg-[linear-gradient(145deg,rgba(15,23,42,0.92)_0%,rgba(19,33,56,0.9)_52%,rgba(24,42,70,0.78)_100%)] p-7 shadow-[0_20px_70px_-35px_rgba(0,0,0,0.95)] md:p-9 lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#f6c53d]/35 bg-[#f6c53d]/10 px-4 py-1.5 text-sm text-[#ffd978]">
              <WandSparkles className="h-4 w-4" />
              Built for mobile-first learning
            </span>

            <h1 className="mt-6 text-4xl font-semibold leading-[1.02] tracking-tight md:text-6xl">
              Zuma AI App
              <span className="mt-2 block text-[#f6c53d]">Now on Play Store.</span>
            </h1>

            <p className="mt-5 max-w-2xl text-base leading-7 text-[#d8e2f1] md:text-lg">
              The official Zuma AI mobile experience is here. Get the latest build directly from 
              the Google Play Store for the best performance and security.
            </p>
          </div>

          <div className="space-y-4 lg:col-span-5">
            <div className="flex flex-col gap-4">
              <Link
                href="https://play.google.com/store/apps/details?id=com.np.zumaaimobile"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-[26px] border border-white/15 bg-[linear-gradient(180deg,rgba(15,27,49,0.95)_0%,rgba(9,20,37,0.95)_100%)] p-5 text-left transition hover:-translate-y-0.5 hover:border-[#60a5fa]/70"
              >
                <div className="relative z-10">
                  <p className="text-sm font-semibold text-[#f8d989]">Google Play Store</p>
                  <p className="mt-1 text-sm text-[#c1ccd9]">Get it on Android</p>
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    className="mt-4 h-[56px] w-auto rounded-md bg-black"
                  />
                </div>
                
                <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:block z-20">
                  <div className="relative group/qr transition-transform duration-500 group-hover:scale-105">
                    <div className="absolute -inset-3 bg-[#f8d989]/10 rounded-2xl blur-xl opacity-100 transition-opacity duration-500" />
                    <div className="relative rounded-2xl border border-[#f8d989]/30 bg-white/5 p-2 backdrop-blur-md transition-colors group-hover:border-[#f8d989]/60">
                      <img 
                        src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://play.google.com/store/apps/details?id=com.np.zumaaimobile&color=f8d989&bgcolor=0f1b31" 
                        alt="QR Code"
                        className="h-20 w-20 opacity-100 transition-all duration-500"
                      />
                      <div className="absolute inset-x-0 -bottom-6 text-center">
                        <span className="text-[10px] font-bold tracking-wider uppercase text-[#f8d989] transition-colors">Scan Me</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#60a5fa]/5 blur-[60px] transition-colors group-hover:bg-[#60a5fa]/10" />

              </Link>

              <button
                type="button"
                onClick={() => showComingSoonToast("Apple App Store")}
                className="group relative overflow-hidden rounded-[26px] border border-white/15 bg-[linear-gradient(180deg,rgba(15,27,49,0.95)_0%,rgba(9,20,37,0.95)_100%)] p-5 text-left transition hover:-translate-y-0.5 hover:border-[#93c5fd]/70"
              >
                <div className="relative z-10">
                  <p className="text-sm font-semibold text-[#f8d989]">Apple App Store</p>
                  <p className="mt-1 text-sm text-[#c1ccd9]">Tap to get launch update</p>
                  <img
                    src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                    alt="Download on the App Store"
                    className="mt-4 h-[56px] w-auto rounded-md"
                  />
                </div>

                <div className="absolute right-10 top-1/2 -translate-y-1/2 hidden md:block opacity-100 transition-all duration-500">
                   <div className="relative">
                      <div className="absolute inset-0 animate-ping rounded-full bg-[#93c5fd]/20" />
                      <div className="relative rounded-full border border-white/20 bg-white/5 p-4 backdrop-blur-sm">
                         <Sparkles className="h-8 w-8 text-[#f8d989]" />
                      </div>
                   </div>
                </div>

                <div className="absolute -right-12 -bottom-12 h-40 w-40 rounded-full bg-[#93c5fd]/5 blur-[60px] transition-colors group-hover:bg-[#93c5fd]/10" />

                <div className="pointer-events-none absolute inset-0 z-20 bg-[#020617]/80" />
                <span className="pointer-events-none absolute right-3 top-3 z-30 inline-flex items-center rounded-full border border-white/20 bg-black/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white">
                  Coming Soon
                </span>
              </button>
            </div>

          </div>
        </div>
      </section>
    </main>
  );
}
